<template>
  <div class="kt-grid kt-grid--ver kt-grid--root">
    <div
      class="kt-grid kt-grid--hor kt-grid--root kt-login kt-login--v6 kt-login--signin"
      :class="{ 'auth-fullwidth': isFullWidthStep }"
      id="kt_login"
    >
      <div class="auth-topbar">
        <router-link :to="{ name: 'login' }" class="auth-topbar__logo">
          <img :src="site.meta.default.logo" alt="Studio Metrodesk" />
        </router-link>
      </div>

      <div
        class="kt-grid__item kt-grid__item--fluid kt-grid kt-grid--desktop kt-grid--ver-desktop kt-grid--hor-tablet-and-mobile"
      >
        <!-- Left / center panel -->
        <div
          class="kt-grid__item kt-grid__item--order-tablet-and-mobile-2 kt-grid kt-grid--hor kt-login__aside"
          :class="{ 'w-100': isFullWidthStep }"
        >
          <div class="kt-login__wrapper">
            <div class="kt-login__container">
              <div class="kt-login__body auth-form">
                <!-- ── Step 1: Email entry + Social sign-in ──────────────── -->
                <div
                  v-if="emailStep"
                  class="kt-login__register col-sm-12 col-md-12 col-lg-10 col-xl-8 col-12 mx-auto"
                >
                  <div class="kt-head px-0 text-center mb-0">
                    <h3 class="kt-title" style="font-size:1.8rem; font-weight:700; color:#1a1a2e;">
                      Get Started Today
                    </h3>
                    <p class="text-muted mt-2" style="font-size:0.9rem;">
                      By signing up, I agree to Studio Metrodesk's
                      <a :href="site.meta.register.terms_and_condition_link" target="_blank">Terms</a>
                      and <a href="#">Privacy Policy</a>
                    </p>
                  </div>

                  <Alert v-if="recaptchaErr" variant="danger">
                    <div class="d-flex align-items-center">
                      <i class="flaticon-questions-circular-button mr-2"></i>
                      {{ recaptchaErr }}
                    </div>
                  </Alert>
                  <Alert v-if="errorMessage" variant="danger">{{ errorMessage }}</Alert>

                  <!-- Social sign-in buttons -->
                  <SocialSignInButtons
                    @success="handleSocialSignIn"
                    @error="handleSocialError"
                  />

                  <!-- Divider -->
                  <div class="auth-divider my-3">
                    <span>Or sign up through email</span>
                  </div>

                  <!-- Email form -->
                  <form @submit.prevent="continueFromEmail">
                    <div class="form-group">
                      <input
                        class="form-control"
                        type="email"
                        placeholder="Enter your email to continue..."
                        v-model="form.email"
                        :class="{ 'is-invalid': hasError('email') }"
                        @input="clearError('email')"
                        required
                      />
                      <span v-if="hasError('email')" class="form-text text-danger">
                        {{ getError('email') }}
                      </span>
                    </div>
                    <div class="form-group">
                      <button
                        type="submit"
                        class="btn btn-brand w-100 btn-elevate"
                        :disabled="busy"
                      >
                        <div class="d-flex justify-content-center align-items-center">
                          <span>Continue</span>
                          <div
                            v-if="busy"
                            class="spinner-border spinner-border-sm ml-2"
                            role="status"
                          >
                            <span class="sr-only">Loading...</span>
                          </div>
                        </div>
                      </button>
                    </div>
                  </form>

                  <div class="text-center mt-2" style="font-size:1rem;">
                    <span class="text-muted">Already have an account?</span>
                    <router-link
                      :to="{ name: 'login' }"
                      class="font-weight-bold ml-2"
                      style="font-size: inherit"
                    >Log in</router-link>
                  </div>
                </div>

                <!-- ── Step 2: Profile completion ─────────────────────────── -->
                <div
                  v-if="profileStep"
                  class="kt-login__register col-sm-12 col-md-12 col-lg-10 col-xl-8 col-12 mx-auto"
                >
                  <div class="kt-head px-0 text-center mb-3">
                    <h3 class="kt-title" style="font-size:1.8rem; font-weight:700; color:#1a1a2e;">
                      Get Started Today
                    </h3>
                    <p class="text-muted mt-2" style="font-size:0.9rem;">
                      You're almost there — create your new account for
                      <strong>{{ form.email }}</strong> by completing these details.
                    </p>
                  </div>

                  <Alert v-if="recaptchaErr" variant="danger">
                    <div class="d-flex align-items-center">
                      <i class="flaticon-questions-circular-button mr-2"></i>
                      {{ recaptchaErr }}
                    </div>
                  </Alert>

                  <form @submit.prevent="continueFromProfile">
                    <div class="form-row">
                      <div class="form-group col-6">
                        <input
                          class="form-control"
                          type="text"
                          placeholder="First Name*"
                          v-model="form.first_name"
                          :class="{ 'is-invalid': hasError('first_name') }"
                          @input="clearError('first_name')"
                          required
                        />
                        <span v-if="hasError('first_name')" class="form-text text-danger">
                          {{ getError('first_name') }}
                        </span>
                      </div>
                      <div class="form-group col-6">
                        <input
                          class="form-control"
                          type="text"
                          placeholder="Last Name*"
                          v-model="form.last_name"
                          :class="{ 'is-invalid': hasError('last_name') }"
                          @input="clearError('last_name')"
                          required
                        />
                        <span v-if="hasError('last_name')" class="form-text text-danger">
                          {{ getError('last_name') }}
                        </span>
                      </div>
                    </div>

                    <div class="form-group">
                      <input
                        ref="phone"
                        v-model="form.phone"
                        class="form-control"
                        placeholder="Phone Number*"
                        type="text"
                        :class="{ 'is-invalid': hasError('phone') }"
                        @blur="validatePhoneNumber"
                        @input="clearError('phone')"
                      />
                      <span v-if="hasError('phone')" class="form-text text-danger">
                        {{ getError('phone') }}
                      </span>
                    </div>

                    <div class="form-group">
                      <div class="password-field">
                        <input
                          class="form-control"
                          :type="showPassword ? 'text' : 'password'"
                          placeholder="Set Password*"
                          v-model="form.password"
                          :class="{ 'is-invalid': hasError('password') }"
                          @input="clearError('password')"
                          required
                        />
                        <button
                          type="button"
                          class="password-toggle"
                          tabindex="-1"
                          @click="showPassword = !showPassword"
                        >
                          <i :class="showPassword ? 'la la-eye-slash' : 'la la-eye'"></i>
                        </button>
                      </div>
                      <span v-if="hasError('password')" class="form-text text-danger">
                        {{ getError('password') }}
                      </span>
                    </div>

                    <div class="form-group">
                      <div class="password-field">
                        <input
                          class="form-control"
                          :type="showConfirmPassword ? 'text' : 'password'"
                          placeholder="Confirm Password*"
                          v-model="confirmPassword"
                          :class="{ 'is-invalid': confirmPasswordError !== '' }"
                          @input="confirmPasswordError = ''"
                          required
                        />
                        <button
                          type="button"
                          class="password-toggle"
                          tabindex="-1"
                          @click="showConfirmPassword = !showConfirmPassword"
                        >
                          <i :class="showConfirmPassword ? 'la la-eye-slash' : 'la la-eye'"></i>
                        </button>
                      </div>
                      <span v-if="confirmPasswordError" class="form-text text-danger">
                        {{ confirmPasswordError }}
                      </span>
                    </div>

                    <div class="form-group">
                      <button
                        type="submit"
                        class="btn btn-brand w-100 btn-elevate"
                        :disabled="busy"
                      >
                        <div class="d-flex justify-content-center align-items-center">
                          <span>Continue</span>
                          <div
                            v-if="busy"
                            class="spinner-border spinner-border-sm ml-2"
                            role="status"
                          >
                            <span class="sr-only">Loading...</span>
                          </div>
                        </div>
                      </button>
                    </div>
                  </form>

                  <p class="text-center text-muted mt-2" style="font-size:0.85rem;">
                    By signing up, I agree to Studio Metrodesk's
                    <a :href="site.meta.register.terms_and_condition_link" target="_blank">Terms</a>
                    and <a href="#">Privacy Policy</a>
                  </p>
                </div>

                <!-- ── Step 3: OTP verification ───────────────────────────── -->
                <div
                  v-if="otpStep"
                  class="otp-panel col-12 col-sm-10 col-md-8 col-lg-6 col-xl-5 mx-auto text-center"
                >
                  <h3 style="font-size:1.8rem; font-weight:700; color:#1a1a2e; margin-bottom:0.5rem;">
                    Check Your Inbox
                  </h3>
                  <p class="text-muted mb-4" style="font-size:0.95rem;">
                    We sent a temporary 6-digit code to <strong>{{ form.email }}</strong>
                  </p>

                  <Alert v-if="otpError" variant="danger">{{ otpError }}</Alert>

                  <OtpInput
                    v-model="otpValue"
                    :disabled="busy"
                    class="mb-4"
                  />

                  <button
                    type="button"
                    class="btn btn-brand w-100 btn-elevate mb-3"
                    :disabled="busy || otpValue.length < 6"
                    @click="submitOtp"
                  >
                    <div class="d-flex justify-content-center align-items-center">
                      <span>Submit</span>
                      <div
                        v-if="busy"
                        class="spinner-border spinner-border-sm ml-2"
                        role="status"
                      >
                        <span class="sr-only">Loading...</span>
                      </div>
                    </div>
                  </button>

                  <p class="text-muted" style="font-size:0.9rem;">
                    Didn't receive code?
                    <a
                      href="#"
                      :class="{ 'text-muted': resendCooldown > 0 }"
                      @click.prevent="resendCode"
                    >
                      {{ resendCooldown > 0 ? `Resend Code (${resendCooldown}s)` : 'Resend Code' }}
                    </a>
                  </p>
                </div>

                <!-- ── Step 4: Success ─────────────────────────────────────── -->
                <div
                  v-if="successStep"
                  class="success-panel col-12 text-center mx-auto"
                >
                  <div class="success-icon mb-4">
                    <!-- Handshake SVG icon -->
                    <svg viewBox="0 0 120 80" fill="none" xmlns="http://www.w3.org/2000/svg" class="handshake-svg">
                      <g stroke="#1a1a2e" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" fill="none">
                        <!-- Left hand -->
                        <path d="M20 50 C20 50 30 42 40 40 L55 38 C58 37.5 60 39 60 42 C60 45 57 46 54 46 L46 46"/>
                        <path d="M46 46 L62 46 C65 46 67 48 67 51 C67 54 65 55 62 55 L50 55"/>
                        <path d="M50 55 L64 55 C67 55 69 57 69 60 C69 63 67 64 64 64 L52 64"/>
                        <path d="M52 64 L63 64 C66 64 68 66 68 69 C68 72 66 73 63 73 L42 73 C36 73 30 70 26 66 L18 58"/>
                        <!-- Right hand -->
                        <path d="M100 50 C100 50 90 42 80 40 L65 38 C62 37.5 60 39 60 42"/>
                        <path d="M60 42 C60 45 63 46 66 46 L74 46"/>
                        <path d="M74 46 L58 46 C55 46 53 48 53 51 C53 54 55 55 58 55 L70 55"/>
                        <path d="M70 55 L56 55 C53 55 51 57 51 60 C51 63 53 64 56 64 L68 64"/>
                        <path d="M68 64 L57 64 C54 64 52 66 52 69 C52 72 54 73 57 73 L78 73 C84 73 90 70 94 66 L102 58"/>
                        <!-- Shake lines -->
                        <line x1="60" y1="20" x2="60" y2="12"/>
                        <line x1="50" y1="22" x2="45" y2="15"/>
                        <line x1="70" y1="22" x2="75" y2="15"/>
                      </g>
                    </svg>
                  </div>
                  <h3 style="font-size:1.6rem; font-weight:700; color:#1a1a2e; margin-bottom:0.75rem;">
                    Email Verified Successfully!
                  </h3>
                  <p class="text-muted" style="font-size:0.95rem;">
                    Your account is now active. You can continue setting up your profile and start submitting projects.
                  </p>
                </div>

              </div>
            </div>
          </div>
        </div>

        <!-- Right hero panel — hidden on OTP and Success steps -->
        <div
          v-if="!isFullWidthStep"
          :style="{ backgroundImage: `url(${banner})` }"
          class="kt-grid__item kt-grid__item--fluid kt-grid__item--center kt-grid kt-grid--ver kt-login__content"
        >
          <div class="kt-login__section">
            <div class="kt-login__block mx-5">
              <h3 class="kt-login__title">
                {{ site.meta.register.hero_section_title }}
              </h3>
              <div class="kt-login__desc">
                {{ site.meta.register.hero_section_description }}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import Alert from '@/components/Alert.vue';
import OtpInput from '@/components/OtpInput.vue';
import SocialSignInButtons from '@/components/SocialSignInButtons.vue';
import intlTelInput from 'intl-tel-input';
import ValidationErrorsMixin from '@/mixins/ValidationErrors.js';
import 'intl-tel-input/build/css/intlTelInput.css';

export default {
  name: 'Register',

  components: {
    Alert,
    OtpInput,
    SocialSignInButtons,
  },

  mixins: [ValidationErrorsMixin],

  data() {
    return {
      banner: '',
      showPassword: false,
      showConfirmPassword: false,

      form: {
        email: '',
        first_name: '',
        last_name: '',
        phone: '',
        password: '',
      },
      confirmPassword: '',
      confirmPasswordError: '',

      otpValue: '',
      otpError: '',
      resendCooldown: 0,
      resendTimer: null,

      errorMessage: '',
      recaptchaErr: '',
      busy: false,

      phoneInput: null,

      emailStep: true,
      profileStep: false,
      otpStep: false,
      successStep: false,
    };
  },

  computed: {
    ...mapGetters({
      site: 'site/site',
    }),
    isFullWidthStep() {
      return this.otpStep || this.successStep;
    },
  },

  mounted() {
    this.banner = this.site.meta.register.banner;
    this.setPageMeta(this.site.meta.register);
  },

  beforeDestroy() {
    clearInterval(this.resendTimer);
  },

  methods: {
    ...mapActions({
      sendVerificationCode: 'auth/sendVerificationCode',
      verifyCode: 'auth/verify',
      register: 'auth/register',
      login: 'auth/login',
      socialSignIn: 'auth/socialSignIn',
    }),

    setPageMeta(meta = {}) {
      const { page_title, page_description, robots_index } = meta;
      if (page_title) document.title = page_title || 'Studio Metrodesk';
      const descEl = document.querySelector('meta[name="description"]');
      if (descEl) descEl.setAttribute('content', page_description || '');
      const robotsEl = document.querySelector('meta[name="robots"]');
      if (robotsEl) robotsEl.setAttribute('content', robots_index === 'noindex' ? 'noindex, nofollow' : 'index, follow');
    },

    // ── Step 1: Email → send OTP → go to profile ──────────────────────────
    continueFromEmail() {
      if (this.busy) return;
      this.busy = true;
      this.clearAllErrors();
      this.errorMessage = '';

      this.sendVerificationCode({ email: this.form.email })
        .then(() => {
          this.emailStep = false;
          this.profileStep = true;
          this.banner = this.site.meta.register.second_banner;

          this.$nextTick(() => {
            if (this.$refs.phone) {
              this.phoneInput = intlTelInput(this.$refs.phone, {
                separateDialCode: true,
                utilsScript:
                  'https://cdnjs.cloudflare.com/ajax/libs/intl-tel-input/17.0.19/js/utils.min.js',
              });
            }
          });
        })
        .catch((error) => {
          if (error.response?.data?.errors) {
            this.errors = error.response.data.errors;
          } else {
            this.errorMessage = error.response?.data?.message || 'Failed to send verification code.';
          }
        })
        .finally(() => {
          this.busy = false;
        });
    },

    // ── Step 2: Profile → validate → go to OTP ───────────────────────────
    continueFromProfile() {
      if (this.busy) return;

      this.confirmPasswordError = '';
      this.clearAllErrors();

      if (!this.form.first_name.trim()) {
        this.errors = { ...this.errors, first_name: ['First name is required.'] };
        return;
      }

      if (!this.form.last_name.trim()) {
        this.errors = { ...this.errors, last_name: ['Last name is required.'] };
        return;
      }

      if (this.form.phone && this.phoneInput && !this.phoneInput.isValidNumber()) {
        this.errors = { ...this.errors, phone: ['Invalid phone number.'] };
        return;
      }

      if (this.form.password !== this.confirmPassword) {
        this.confirmPasswordError = 'The password confirmation does not match.';
        return;
      }

      if (this.form.password.length < 8) {
        this.errors = { ...this.errors, password: ['The password must be at least 8 characters.'] };
        return;
      }

      this.profileStep = false;
      this.otpStep = true;
    },

    validatePhoneNumber() {
      if (this.form.phone && this.phoneInput && !this.phoneInput.isValidNumber()) {
        this.errors = { ...this.errors, phone: ['Invalid phone number.'] };
      }
    },

    // ── Step 3: OTP → verify → reCAPTCHA → register → login → success ───
    async submitOtp() {
      if (this.busy) return;
      this.busy = true;
      this.otpError = '';

      try {
        const verificationPayload = {
          email: this.form.email,
          verification_code: this.otpValue,
        };

        const data = await this.verifyCode(verificationPayload);
        if (!data.data.success) {
          this.otpError = 'Verification code mismatched! Please try again.';
          return;
        }

        const token = await this.$recaptcha('register');

        await this.register({
          first_name: this.form.first_name.trim(),
          last_name: this.form.last_name.trim(),
          email: this.form.email,
          phone: this.phoneInput ? this.phoneInput.getNumber() : this.form.phone,
          password: this.form.password,
          g_recaptcha_response: token,
        });

        this.triggerGtmDataLayer();

        await this.login({
          credential: { email: this.form.email, password: this.form.password },
          remember: null,
        });

        this.otpStep = false;
        this.successStep = true;

        if (window.oaiq) {
          window.oaiq("measure", "registration_completed", { type: "customer_action" });
        }

        setTimeout(() => {
          this.$router.replace({ name: 'home' });
        }, 2000);
      } catch (error) {
        if (error?.response?.data?.errors?.g_recaptcha_response) {
          this.recaptchaErr = error.response.data.errors.g_recaptcha_response[0];
          this.otpStep = false;
          this.profileStep = true;
        } else if (error?.response?.data?.errors) {
          this.errors = error.response.data.errors;
          this.otpStep = false;
          if (this.errors.email) {
            this.emailStep = true;
          } else {
            this.profileStep = true;
          }
        } else {
          this.otpError = error?.response?.data?.message || 'Something went wrong. Please try again.';
        }
      } finally {
        if (!this.successStep) this.busy = false;
      }
    },

    resendCode() {
      if (this.resendCooldown > 0 || this.busy) return;

      this.sendVerificationCode({ email: this.form.email })
        .then(() => {
          this.startResendCooldown();
        })
        .catch(() => {
          this.otpError = 'Failed to resend code. Please try again.';
        });
    },

    startResendCooldown(seconds = 60) {
      this.resendCooldown = seconds;
      this.resendTimer = setInterval(() => {
        this.resendCooldown--;
        if (this.resendCooldown <= 0) {
          clearInterval(this.resendTimer);
        }
      }, 1000);
    },

    // ── Social sign-in ────────────────────────────────────────────────────
    handleSocialSignIn({ provider, providerToken, firstName, lastName }) {
      if (this.busy) return;
      this.busy = true;
      this.errorMessage = '';

      this.socialSignIn({ provider, providerToken, firstName, lastName })
        .then(() => {
          this.$router.replace({ name: 'home' });
        })
        .catch((error) => {
          const status = error?.response?.status;
          if (status === 422) {
            this.errorMessage = 'Invalid or expired token. Please try again.';
          } else if (status === 419) {
            this.errorMessage = 'Your account has been locked. Please contact support.';
          } else if (status === 403) {
            this.errorMessage = 'Access denied for this site.';
          } else {
            this.errorMessage = 'Something went wrong. Please try again.';
          }
        })
        .finally(() => {
          this.busy = false;
        });
    },

    handleSocialError({ message }) {
      this.errorMessage = message;
    },

    // ── Utils ─────────────────────────────────────────────────────────────
    triggerGtmDataLayer() {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({ event: 'signup' });
    },
  },
};
</script>

<style src="../../assets/css/auth.css"></style>
<style>
.grecaptcha-badge {
  visibility: hidden !important;
}

/* Shared divider (same as Login) */
.auth-divider {
  display: flex;
  align-items: center;
  text-align: center;
  color: #9ca3af;
  font-size: 0.85rem;
}
.auth-divider::before,
.auth-divider::after {
  content: '';
  flex: 1;
  border-bottom: 1px solid #e5e7eb;
}
.auth-divider::before { margin-right: 0.75em; }
.auth-divider::after  { margin-left:  0.75em; }

/* Password field with toggle */
.password-field {
  position: relative;
}
.password-field .form-control {
  padding-right: 2.5rem;
}
.password-toggle {
  position: absolute;
  right: 0.75rem;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  cursor: pointer;
  color: #9ca3af;
  padding: 0;
  line-height: 1;
}
.password-toggle:hover { color: #6b7280; }

/* Full-width mode for OTP & Success steps */
.auth-fullwidth .kt-login__aside {
  width: 100% !important;
  max-width: 100% !important;
}
.auth-fullwidth .kt-login__container {
  justify-content: center;
}

/* OTP panel */
.otp-panel {
  padding: 2rem 1rem;
}

/* Success panel */
.success-panel {
  padding: 3rem 1rem;
  max-width: 480px;
}
.success-icon {
  display: flex;
  justify-content: center;
}
.handshake-svg {
  width: 120px;
  height: 80px;
}

/* OTP + Success full-width background gradient */
.auth-fullwidth {
  background: linear-gradient(160deg, #f0f4ff 0%, #e8f5f0 50%, #f0f8ff 100%) !important;
}
</style>
